import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import _ from "lodash";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import {
  DevicesSets,
  rejectRequests,
  rejectRoaming,
  rejectUnassigned,
} from "../../../../helpers/devices.helpers";
import { TabletMobile, Desktop } from "../../../../hocs/Responsive";
import { SpsonFluidPanel } from "../../../spson/spsonPanel/SpsonPanel";
import { isSPSONAvailable } from "../../../../store/account";
import { RequestedDevicesTable } from "./requestedDevicesTable";
import { OnlineDevicesTable } from "./onlineDevicesTable";
import {
  ProfileRoamingDevicesTable,
  ProfileNotRoamingDevicesTable,
} from "../shared";
import { translateMap } from "../../../../utils/translateMap";
import { ProfileGroups } from "../../../../store/profiles/fixedProfiles";
import { anyNewRequests } from "../../../../store/devices/devices.selectors";
import {
  getDefaultProfileId,
  hasDefaultProfileNonRoamingDevices,
} from "../../../../store/profiles/profiles.selectors";
import { TabList, createDeviceLinkTab } from "../../../../components/tabList";
import { HorizontalScrollTabs } from "../../../../components/horizontalScrollTabs/HorizontalScrollTabs";
import { withSettingDevicesGuard } from "../../withSettingsDevicesGuard";

import "../../../../components/devices/TableDevices.scss";

const TABS_SETTINGS = [
  {
    value: DevicesSets.Requests,
    name: "new_requests",
    url: "new-requests",
    description: <FormattedMessage id={"requested_devices_description"} />,
    content: <RequestedDevicesTable />,
  },
  {
    value: DevicesSets.Discovered,
    name: "online",
    url: "online",
    description: <FormattedMessage id="online_devices_description" />,
    content: <OnlineDevicesTable />,
  },
  {
    value: DevicesSets.NotRoaming,
    name: "unassigned",
    url: "unassigned",
    description: <FormattedMessage id={"unassigned_devices_description"} />,
    content: <DefaultProfileNotRoamingDeviceTable />,
  },
  {
    value: DevicesSets.Roaming,
    name: "roaming",
    url: "roaming",
    description: <FormattedMessage id="roaming_devices_description" />,
    content: (
      <ProfileRoamingDevicesTable profileName={ProfileGroups.Visitors} />
    ),
  },
];

function DefaultProfileNotRoamingDeviceTable() {
  const id = useSelector(getDefaultProfileId);

  return (
    <ProfileNotRoamingDevicesTable
      profileName={ProfileGroups.Visitors}
      profileId={id}
    />
  );
}

const getDescription = (selectedTab, tabs) => {
  const currentTab = _.find(tabs, { url: selectedTab });

  return currentTab
    ? currentTab.description
    : _.first(TABS_SETTINGS).description;
};

const getSelectedTab = (url) => _.last(url.split("/"));

export const DefaultProfileContent = withSettingDevicesGuard(
  function DefaultProfileContent() {
    const spsonAvailable = useSelector(isSPSONAvailable);
    const hasNewRequests = useSelector(anyNewRequests);
    const hasUnassigned = useSelector(hasDefaultProfileNonRoamingDevices);
    const intl = useIntl();
    const { pathname } = useLocation();
    const selectedTab = getSelectedTab(pathname);

    const filteredTabs = useMemo(() => {
      const filters = [
        !spsonAvailable && rejectRoaming,
        !hasNewRequests && rejectRequests,
        !hasUnassigned && rejectUnassigned,
      ].filter(Boolean);

      return _.flow(filters)(TABS_SETTINGS);
    }, [spsonAvailable, hasNewRequests, hasUnassigned]);

    const routes = useMemo(() => {
      return translateMap(filteredTabs, intl);
    }, [filteredTabs, intl]);

    const tabs = useMemo(() => {
      return filteredTabs.map((tab) => createDeviceLinkTab(tab, intl));
    }, [filteredTabs, intl]);

    const showSPSON = spsonAvailable && selectedTab === DevicesSets.Roaming;

    return (
      <section>
        <h2 className={"sr-only"}>
          <FormattedMessage id={"manage_devices"} />
        </h2>

        <Desktop>{showSPSON && renderSPSONPanel()}</Desktop>
        <section className={`devices__layout devices--${selectedTab}`}>
          <div>
            <h2>
              <FormattedMessage id={"devices"} />
            </h2>
            <p className={"settings-page__description"}>
              {getDescription(selectedTab, TABS_SETTINGS)}
            </p>
          </div>
          <Desktop>
            <TabList items={tabs} />
          </Desktop>
          <TabletMobile>
            <HorizontalScrollTabs tabs={tabs} />
            {showSPSON && renderSPSONPanel()}
          </TabletMobile>
          <Routes>
            {routes.map((tab) => (
              <Route path={tab.url} key={tab.name} element={tab.content} />
            ))}

            <Route path="*" element={<Navigate to={_.head(routes).url} />} />
          </Routes>
        </section>
      </section>
    );
  }
);

const renderSPSONPanel = () => {
  return <SpsonFluidPanel className="devices__spson-panel" />;
};
